import { Api } from "../../Api";
import { Status } from "../../DataFrame";
import { IDIService } from "../../IDIService";
import { FieldChangeResult, ResponseResult } from "../FieldChangeResult";
import { ILoggerService } from "../Logging/ILoggerService";
import { LoggerService } from "../Logging/LoggerService";
import { ChannelsGroupsService } from "./ChannelsGroupsService";
import { DeviceChannel } from "./DeviceChannel";
import { DevicesService } from "./DevicesService";
import { IDevice } from "./IDevice";
import { IDeviceChannel } from "./IDeviceChannel";
import { IDevicesService } from "./IDevicesService";

export interface IChannelsGroupsService extends IDIService {
    WaitForSynchronizationAsync(): Promise<void>;
    RemoveGroupAsync(group: IChannelsGroup): Promise<Status>;
    RemoveGroupByGuidAsync(groupGuid: string): Promise<Status>;
    GetGroupByGuid(guid: string): IChannelsGroup | null;
    GetGroupsAsync(): Promise<IChannelsGroup[]>;

    IsGroupsAndObjectsOrderingAvailable(): boolean;
    MoveGroupPositionAsync(group: IChannelsGroup, newPosition: number): Promise<Status>;
    ChangeGroupsOrderAsync(orderedGroups: IChannelsGroup[]): Promise<Status>;
    MoveObjectInGroupPositionAsync(group: IChannelsGroup, channel: IDeviceChannel, newPosition: number): Promise<Status>;
    ChangeObjectsPositionsInGroupAsync(group: IChannelsGroup, channels: IDeviceChannel[]): Promise<Status>;

    SetObjectsInGroupAndOrderAsync(group: IChannelsGroup, objects: Array<IDevice | IDeviceChannel>): Promise<ResponseResult<SetObjectsInGroupAndOrderResponseCode>>;

    AddDeviceChannelToGroupAsync(channel: IDeviceChannel, group: IChannelsGroup): Promise<Status>;
    AddDeviceChannelToGroupByGuidAsync(channel: IDeviceChannel, groupGuid: string): Promise<Status>;

    RemoveDeviceChannelFromGroupAsync(channel: IDeviceChannel, group: IChannelsGroup): Promise<Status>;
    RemoveDeviceChannelFromGroupByGuidAsync(channel: IDeviceChannel, groupGuid: string): Promise<Status>;

    AddNewGroupAsync(name: string): Promise<IChannelsGroup | ResponseResult<AddNewGroupErrorCode>>;
    AddNewGroupWithIconAsync(name: string, iconName: string): Promise<IChannelsGroup | ResponseResult<AddNewGroupErrorCode>>;
    UpsertGroupWithDevicesChannelsAsync(group: IChannelsGroup, deviceChannels: IDeviceChannel[]): Promise<IChannelsGroup | ResponseResult<AddNewGroupErrorCode>>;
    UpdateGroupWithDevicesChannelsAsync(groupGuid: string, deviceChannels: IDeviceChannel[]): Promise<Status>;

    ChangeGroupNameAsync(group: IChannelsGroup, name: string): Promise<ResponseResult<AddNewGroupErrorCode>>;
    ChangeGroupIconNameAsync(group: IChannelsGroup, iconName: string): Promise<ResponseResult<AddNewGroupErrorCode>>;
    ChangeGroupNameAndIconAsync(group: IChannelsGroup, name: string, iconName: string): Promise<ResponseResult<AddNewGroupErrorCode>>;
    ChangeGroupNameByGuidAsync(groupGuid: string, name: string): Promise<ResponseResult<AddNewGroupErrorCode>>;
    ChangeGroupIconNameByGuidAsync(groupGuid: string, iconName: string): Promise<ResponseResult<AddNewGroupErrorCode>>;
    ChangeGroupNameAndIconByGuidAsync(groupGuid: string, name: string, iconName: string): Promise<ResponseResult<AddNewGroupErrorCode>>;
}

export enum SetObjectsInGroupAndOrderResponseCode {
    Success = "Success",
    GroupDoesNotExists = "GroupDoesNotExists",
    ObjectDoesNotExists = "ObjectDoesNotExists",
    FatalError = "FatalError",
    FunctionNotSupported = "FunctionNotSupported",
}

export enum AddNewGroupErrorCode {
    Success = "Success",
    NoPermissions = "NoPermissions",
    NameToShort = "NameToShort",
    NameAlreadyExists = "NameAlreadyExists",
    OtherError = "OtherError",
    UnknownError = "UnknownError"
}

export interface IChannelsGroup {
    get Guid(): string;
    get Name(): string;
    get IconName(): string;
    get OrderPosition(): number;
    get ObjectsOrder(): Record<number, string>;
    GetDevicesChannelsInGroupAsync(withScenes: boolean): Promise<IDeviceChannel[]>;
    ChangeNameAsync(name: string): Promise<ResponseResult<AddNewGroupErrorCode>>;
    ChangeIconNameAsync(name: string): Promise<ResponseResult<AddNewGroupErrorCode>>;
}

export class ChannelsGroup implements IChannelsGroup {
    private _guid: string = "";
    private _name: string = "";
    private _iconName: string = "";
    private _position: number = 0;
    private _objectsOrder: Record<number, string> = {};
    private static _devicesService: DevicesService | null;

    constructor() {
        if (ChannelsGroup._devicesService == null)
            ChannelsGroup._devicesService = Api.Get<DevicesService>(DevicesService.ServiceName);
    }

    async GetDevicesChannelsInGroupAsync(withScenes: boolean = false): Promise<IDeviceChannel[]> {
        let channels: IDeviceChannel[] = [];
        let sortedChannels: IDeviceChannel[] = [];
        const devs = await ChannelsGroup._devicesService!.GetDevicesAsync(withScenes);

        if (this._objectsOrder === undefined || this._objectsOrder === null || Object.entries(this._objectsOrder).length == 0) {
            Api.Get<ILoggerService>(LoggerService.ServiceName).Error("Old controller API fallback to old groups API.");

            for (let device of devs) {
                for (let channel of device.Channels.where(channel => channel.ChannelGroups.any(group => group === this._guid))) {
                    sortedChannels.push(channel);
                }
            }

            return sortedChannels;
        }
        
        const sortedKeys = Object.keys(this._objectsOrder)
            .map(Number) // convert strings to numbers
            .sort((a, b) => a - b); // sort by numeric values

        Object.values(this._objectsOrder).forEach(value => {
            const channel = devs.select(a => a.Channels.where(b => b.ChannelId === value));

            if (channel !== null)
                for (let c of channel)
                    for (let ch of c)
                        channels.push(ch);
        });

        for (let s of sortedKeys) {
            const ch = channels.firstOrDefault(a => a.ChannelId === this._objectsOrder[s]);

            if (ch !== null)
                sortedChannels.push(ch);
        }

        return sortedChannels;
    }

    get Guid(): string {
        return this._guid;
    }

    set Guid(value: string) {
        this._guid = value;
    }

    get Name(): string {
        return this._name;
    }

    set Name(value: string) {
        this._name = value;
    }

    get IconName(): string {
        return this._iconName;
    }

    set IconName(value: string) {
        this._iconName = value;
    }

    get OrderPosition(): number {
        return this._position;
    }

    set OrderPosition(value: number) {
        this._position = value;
    }

    get ObjectsOrder(): Record<number, string> {
        return this._objectsOrder;
    }

    set ObjectsOrder(value: Record<number, string>) {
        this._objectsOrder = value;
    }

    ChangeNameAsync(name: string): Promise<ResponseResult<AddNewGroupErrorCode>> {
        return Api.Get<IChannelsGroupsService>(ChannelsGroupsService.ServiceName).ChangeGroupNameAsync(this, name);
    }

    ChangeIconNameAsync(iconName: string): Promise<ResponseResult<AddNewGroupErrorCode>> {
        return Api.Get<IChannelsGroupsService>(ChannelsGroupsService.ServiceName).ChangeGroupIconNameAsync(this, iconName);
    }
}